import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building,
  Home,
  Calendar,
  Shield,
  FileText,
  CreditCard,
  Receipt,
  MessageSquare,
  Megaphone,
  Settings,
  Mail,
  Flag,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type AdminSidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  onClose: () => void;
  isMobileDrawer?: boolean;
};

type NavItem  = { label: string; to: string; icon: React.ElementType };
type NavSection = { title: string; items: NavItem[] };

const NAV_SECTIONS: NavSection[] = [
  {
    title: "Overview",
    items: [
      { label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
    ],
  },
  {
    title: "Platform",
    items: [
      { label: "Users",     to: "/admin/users",     icon: Users    },
      { label: "Buildings", to: "/admin/buildings", icon: Building },
      { label: "Units",     to: "/admin/units",     icon: Home     },
      { label: "Bookings",  to: "/admin/bookings",  icon: Calendar },
    ],
  },
  {
    title: "Compliance",
    items: [
      { label: "Compliance Rules", to: "/admin/compliance",  icon: Shield   },
      { label: "Audit Logs",       to: "/admin/audit-logs",  icon: FileText },
    ],
  },
  {
    title: "Revenue",
    items: [
      { label: "Subscriptions", to: "/admin/subscriptions", icon: CreditCard },
      { label: "Invoices",      to: "/admin/invoices",      icon: Receipt    },
    ],
  },
  {
    title: "Communication",
    items: [
      { label: "Messages",      to: "/admin/messages",      icon: MessageSquare },
      { label: "Announcements", to: "/admin/announcements", icon: Megaphone     },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Settings",      to: "/admin/settings",          icon: Settings },
      { label: "Email Config",  to: "/admin/settings/email",    icon: Mail     },
      { label: "Feature Flags", to: "/admin/settings/features", icon: Flag     },
    ],
  },
];

// ---------------------------------------------------------------------------
// Single nav item — icon + label, collapses to icon-only when `collapsed`
// ---------------------------------------------------------------------------

function SidebarNavItem({
  item,
  collapsed,
  onClose,
}: {
  item: NavItem;
  collapsed: boolean;
  onClose: () => void;
}) {
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/admin/dashboard"}
      onClick={onClose}          // close mobile drawer on navigation
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
          collapsed ? "justify-center px-2" : "",
          isActive
            ? "bg-white/10 text-white"
            : "text-gray-400 hover:bg-white/5 hover:text-gray-200",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

// ---------------------------------------------------------------------------
// AdminSidebar
// ---------------------------------------------------------------------------

export function AdminSidebar({
  collapsed,
  onToggle,
  onClose,
  isMobileDrawer = false,
}: AdminSidebarProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          "flex flex-col h-full bg-[#0F1117] text-white transition-all duration-200 shrink-0",
          // Mobile drawer is always full-width (w-72 set by parent)
          isMobileDrawer ? "w-full" : collapsed ? "w-16" : "w-64",
        )}
      >
        {/* ── Logo / header ─────────────────────────────────────────── */}
        <div
          className={cn(
            "flex items-center gap-2 px-4 py-5 border-b border-white/10",
            !isMobileDrawer && collapsed ? "justify-center px-2" : "",
          )}
        >
          {!isMobileDrawer && collapsed ? (
            <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center text-white text-sm font-bold select-none">
              SK
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col min-w-0">
                <span className="text-white font-bold text-base leading-tight tracking-tight">
                  Stratakey
                </span>
                <span className="text-gray-400 text-xs leading-tight">Admin Console</span>
              </div>
              {/* Close button — mobile drawer only */}
              {isMobileDrawer && (
                <button
                  onClick={onClose}
                  className="ml-2 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Navigation ─────────────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title}>
              {(!collapsed || isMobileDrawer) && (
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <SidebarNavItem
                      item={item}
                      collapsed={!isMobileDrawer && collapsed}
                      onClose={onClose}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* ── Collapse toggle (desktop only) ─────────────────────────── */}
        {!isMobileDrawer && (
          <div className="border-t border-white/10 p-2">
            <button
              onClick={onToggle}
              className={cn(
                "w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-400",
                "hover:bg-white/5 hover:text-gray-200 transition-colors",
                collapsed ? "justify-center px-2" : "",
              )}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  <span className="whitespace-nowrap">Collapse</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
